/**
 * GrammarHarnessReportButton — the "Report rendering issue" affordance shown
 * in fallback panels for renderable parse failures.
 *
 * Behaviour: button → AlertDialog with a buffer-text preview and a clear
 * "this never leaves your machine" reassurance → on Confirm, calls
 * `reportGrammarIssue` and shows a success toast with the saved file path
 * (or an error toast if the IPC bridge is missing / write failed).
 */

import { useState } from 'react';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../../components/ui/alert-dialog';
import { Button } from '../../../../components/ui/button';
import { toast } from '../../../../components/ui/toaster';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import {
  reportGrammarIssue,
  type ReportGrammarIssueContext,
  type ReportGrammarIssueDeps,
} from './reportGrammarIssue';

const PREVIEW_MAX_CHARS = 320;

export type GrammarHarnessReportButtonProps = Omit<
  ReportGrammarIssueContext,
  'api'
> & {
  api?: ReportGrammarIssueDeps;
};

function previewBuffer(bufferText: string): string {
  if (bufferText.length <= PREVIEW_MAX_CHARS) return bufferText;
  return `${bufferText.slice(0, PREVIEW_MAX_CHARS)}…`;
}

export function GrammarHarnessReportButton(props: GrammarHarnessReportButtonProps) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { track } = useAnalytics();

  async function handleConfirm() {
    if (submitting) return;
    setSubmitting(true);
    const result = await reportGrammarIssue({
      reason: props.reason,
      bufferText: props.bufferText,
      dialect: props.dialect,
      statementId: props.statementId,
      diagnostic: props.diagnostic,
      api: props.api,
    });
    setSubmitting(false);
    setOpen(false);
    if (result.success && result.filePath) {
      track('feature:vqe_harness_report', { reason: props.reason });
      toast.success({
        title: 'Rendering report saved',
        description: `Saved locally to ${result.filePath}`,
      });
    } else {
      toast.error({
        title: 'Could not save report',
        description: result.error ?? 'Unknown error writing the rendering report.',
      });
    }
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid="vqe-grammar-harness-report-button"
      >
        Report rendering issue
      </Button>

      <AlertDialog open={open} onOpenChange={(next) => !submitting && setOpen(next)}>
        <AlertDialogContent data-testid="vqe-grammar-harness-report-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Report rendering issue?</AlertDialogTitle>
            <AlertDialogDescription>
              We’ll save a copy of this statement to your local Jam SQL data folder so
              the grammar team can reproduce the rendering anomaly. <strong>The report
              never leaves your machine</strong> unless you choose to share it later.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3 text-xs">
            <dl
              className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-muted-foreground"
              data-testid="vqe-grammar-harness-report-meta"
            >
              <dt className="font-medium">Reason</dt>
              <dd className="font-mono">{props.reason}</dd>
              <dt className="font-medium">Dialect</dt>
              <dd className="font-mono">{props.dialect}</dd>
              <dt className="font-medium">Diagnostic</dt>
              <dd className="font-mono break-words">{props.diagnostic}</dd>
            </dl>
            <div>
              <p className="mb-1 font-medium text-muted-foreground">Buffer preview</p>
              <pre
                className="max-h-40 overflow-auto rounded-md border border-border bg-muted/40 p-3 font-mono text-[11px] leading-relaxed"
                data-testid="vqe-grammar-harness-report-preview"
              >
                {previewBuffer(props.bufferText)}
              </pre>
            </div>
          </div>

          <AlertDialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOpen(false)}
              disabled={submitting}
              data-testid="vqe-grammar-harness-report-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleConfirm}
              disabled={submitting}
              data-testid="vqe-grammar-harness-report-confirm"
            >
              {submitting ? 'Saving…' : 'Save report locally'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
